/** Header teleport target for marketplace pill search autocomplete. */
export const PILL_SEARCH_HEADER_ID = "header-searchbox-mobile";

export const PILL_SEARCH_PANEL_OPEN_CLASS = "header__searchbox-mobile--panel-open";

const PILL_SEARCH_PANEL_CLASS = "aa-Panel--pill-search";
const PILL_SEARCH_PANEL_SELECTOR = `.aa-Panel.${PILL_SEARCH_PANEL_CLASS}`;
const PILL_SEARCH_PANEL_GAP_PX = 6;
const PILL_SEARCH_PANEL_MAX_HEIGHT_PX = 400;

/** Blade + Vue home hero autocomplete container ids. */
const HOME_SEARCH_CONTAINER_IDS = new Set([
    "home-autocomplete",
    "search-autocomplete-home",
]);

export const HOME_SEARCH_BOX_OPEN_CLASS = "search-box--panel-open";

// Home hero panel uses Algolia's native positioning (no viewport anchoring),
// so only the open-state class is needed for the flush rounded-corner styling.
const HOME_SEARCH_PANEL_CLASS = "aa-Panel--home-search";

const VIEWPORT_ANCHORED_CLASS = "aa-Panel--viewport-anchored";

type ViewportAnchorSync = {
    anchor: HTMLElement;
    panelSelector: string;
    marginTop: number;
    maxLayoutHeight: number;
};

let viewportAnchorSync: ViewportAnchorSync | null = null;
let viewportAnchorCleanup: (() => void) | null = null;
let viewportAnchorPanel: HTMLElement | null = null;

export function isPillSearchAutocompleteContainer(
    containerId: string,
): boolean {
    const el = document.getElementById(containerId);
    return !!el?.closest(`#${PILL_SEARCH_HEADER_ID}`);
}

export function isHomeSearchAutocompleteContainer(
    containerId: string,
): boolean {
    if (HOME_SEARCH_CONTAINER_IDS.has(containerId)) {
        return true;
    }

    const el = document.getElementById(containerId);
    return !!el?.closest(".hero .search-box");
}

function resetViewportAnchoredPanel(panel: HTMLElement): void {
    panel.classList.remove(VIEWPORT_ANCHORED_CLASS);
    panel.style.position = "";
    panel.style.top = "";
    panel.style.left = "";
    panel.style.right = "";
    panel.style.width = "";
    panel.style.maxWidth = "";

    const layout = panel.querySelector<HTMLElement>(".aa-PanelLayout");
    if (layout) {
        layout.style.maxHeight = "";
    }
}

function positionViewportAnchoredPanel(
    anchor: HTMLElement,
    panel: HTMLElement,
    marginTop: number,
    maxLayoutHeight: number,
): void {
    const rect = anchor.getBoundingClientRect();
    const top = Math.round(rect.bottom + marginTop);
    const width = Math.round(Math.min(rect.width, window.innerWidth - 24));
    const left = Math.round(
        Math.max(12, Math.min(rect.left, window.innerWidth - width - 12)),
    );
    const maxHeight = Math.max(
        0,
        Math.min(maxLayoutHeight, window.innerHeight - top - 16),
    );

    panel.classList.add(VIEWPORT_ANCHORED_CLASS);
    panel.style.position = "fixed";
    panel.style.top = `${top}px`;
    panel.style.left = `${left}px`;
    panel.style.right = "auto";
    panel.style.width = `${width}px`;
    panel.style.maxWidth = `${width}px`;

    const layout = panel.querySelector<HTMLElement>(".aa-PanelLayout");
    if (layout) {
        layout.style.maxHeight = `${maxHeight}px`;
    }
}

function syncViewportAnchoredPanel(): void {
    if (!viewportAnchorSync) {
        return;
    }

    const panel =
        document.querySelector<HTMLElement>(
            viewportAnchorSync.panelSelector,
        ) ?? viewportAnchorPanel;

    if (!panel || !document.body.contains(panel)) {
        return;
    }

    viewportAnchorPanel = panel;
    positionViewportAnchoredPanel(
        viewportAnchorSync.anchor,
        panel,
        viewportAnchorSync.marginTop,
        viewportAnchorSync.maxLayoutHeight,
    );
}

function stopViewportAnchorSync(): void {
    viewportAnchorCleanup?.();
    viewportAnchorCleanup = null;

    if (viewportAnchorPanel) {
        resetViewportAnchoredPanel(viewportAnchorPanel);
    }

    viewportAnchorSync = null;
    viewportAnchorPanel = null;
}

function startViewportAnchorSync(options: ViewportAnchorSync): void {
    stopViewportAnchorSync();
    viewportAnchorSync = options;

    const observer = new MutationObserver(() => {
        syncViewportAnchoredPanel();
    });
    observer.observe(document.body, { childList: true, subtree: true });

    const onViewportChange = () => {
        syncViewportAnchoredPanel();
        // Algolia repositions on resize via rAF — run again after it does.
        requestAnimationFrame(syncViewportAnchoredPanel);
    };

    window.addEventListener("scroll", onViewportChange, true);
    window.addEventListener("resize", onViewportChange);

    viewportAnchorCleanup = () => {
        observer.disconnect();
        window.removeEventListener("scroll", onViewportChange, true);
        window.removeEventListener("resize", onViewportChange);
    };

    requestAnimationFrame(syncViewportAnchoredPanel);
    requestAnimationFrame(() => {
        requestAnimationFrame(syncViewportAnchoredPanel);
    });
}

function handleSearchPanelOpenChange(
    isOpen: boolean,
    anchor: HTMLElement | null | undefined,
    panelSelector: string,
    marginTop: number,
    maxLayoutHeight: number,
): void {
    if (!isOpen || !anchor) {
        stopViewportAnchorSync();
        return;
    }

    startViewportAnchorSync({
        anchor,
        panelSelector,
        marginTop,
        maxLayoutHeight,
    });
}

export function clearPillSearchPanelOpenState(): void {
    stopViewportAnchorSync();
    document
        .getElementById(PILL_SEARCH_HEADER_ID)
        ?.classList.remove(PILL_SEARCH_PANEL_OPEN_CLASS);
}

export function clearHomeSearchPanelOpenState(): void {
    stopViewportAnchorSync();
    document
        .querySelector(".hero .search-box")
        ?.classList.remove(HOME_SEARCH_BOX_OPEN_CLASS);
}

export function pillSearchPanelLiantOptions(): {
    classNames: { panel: string };
    onStateChange: (params: { state: { isOpen: boolean } }) => void;
} {
    const searchbox = () => document.getElementById(PILL_SEARCH_HEADER_ID);

    return {
        classNames: {
            panel: `aa-Panel ${PILL_SEARCH_PANEL_CLASS}`,
        },
        onStateChange({ state }) {
            const searchboxEl = searchbox();
            searchboxEl?.classList.toggle(
                PILL_SEARCH_PANEL_OPEN_CLASS,
                state.isOpen,
            );
            handleSearchPanelOpenChange(
                state.isOpen,
                searchboxEl?.querySelector<HTMLElement>(".aa-Form"),
                PILL_SEARCH_PANEL_SELECTOR,
                PILL_SEARCH_PANEL_GAP_PX,
                PILL_SEARCH_PANEL_MAX_HEIGHT_PX,
            );
        },
    };
}

export function homeSearchPanelLiantOptions(containerId: string): {
    classNames: { panel: string };
    onStateChange: (params: { state: { isOpen: boolean } }) => void;
} {
    const searchBox = () =>
        document.getElementById(containerId)?.closest(".search-box") ?? null;

    return {
        classNames: {
            panel: `aa-Panel ${HOME_SEARCH_PANEL_CLASS}`,
        },
        onStateChange({ state }) {
            const searchBoxEl = searchBox();
            searchBoxEl?.classList.toggle(
                HOME_SEARCH_BOX_OPEN_CLASS,
                state.isOpen,
            );
            if (!state.isOpen) {
                stopViewportAnchorSync();
            }
        },
    };
}

export function resolveSearchPanelLiantOptions(
    containerId: string,
): ReturnType<typeof pillSearchPanelLiantOptions> | null {
    if (isPillSearchAutocompleteContainer(containerId)) {
        return pillSearchPanelLiantOptions();
    }

    if (isHomeSearchAutocompleteContainer(containerId)) {
        return homeSearchPanelLiantOptions(containerId);
    }

    return null;
}
